import { createElement, Fragment, type ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import type { MessageId, MessageText, TagRenderer } from '../ids';

// Every mounted provider registers here; msg() reads the innermost one mounted last. A provider
// that unmounts removes only itself, so one wrapped in another never leaves msg() without a catalog.
const mounted: IntlShape[] = [];

export function registerIntl(intl: IntlShape) {
  if (!mounted.includes(intl)) {
    mounted.push(intl);
  }
}

export function unregisterIntl(intl: IntlShape) {
  const index = mounted.indexOf(intl);
  if (index !== -1) {
    mounted.splice(index, 1);
  }
}

export function getIntl(): IntlShape {
  const current = mounted.at(-1);
  if (current === undefined) {
    throw new Error('msg() was called before <MessagesProvider> mounted.');
  }
  return current;
}

function report(error: Error) {
  if (process.env.NODE_ENV === 'test') {
    throw error;
  }
  if (process.env.NODE_ENV !== 'production') {
    // oxlint-disable-next-line no-console -- DEV signal; production swallows formatter failures
    console.error(error);
  }
}

export function handleIntlError(error: Error) {
  report(error);
}

// Never renders a raw id: a missing key is loud in DEV and test, and empty in production.
function hasMessage(intl: IntlShape, id: string) {
  if (Object.hasOwn(intl.messages, id)) {
    return true;
  }
  report(new Error(`Missing message: ${id}`));
  return false;
}

export type PlainValues = Record<string, string | number>;
export type RichValues = Record<string, string | number | TagRenderer>;

// The return type is the catalog's promise, not the formatter's: a static id resolves to its
// catalog literal because the catalog is the provider's message source. The one exception is a
// key the mounted catalog lacks, which throws in test, logs in development, and renders '' in
// production so a raw id never reaches users.
// `values` arrives as the caller's MessageValues<K>, which TypeScript cannot resolve for a generic K,
// so the plain-values shape is asserted here rather than at every msg() call.
export function formatCatalogMessage<K extends MessageId>(
  intl: IntlShape,
  id: K,
  values?: Record<string, unknown>,
): MessageText<K> {
  if (!hasMessage(intl, id)) {
    return '' as MessageText<K>;
  }
  return intl.formatMessage({ id }, values as PlainValues | undefined) as MessageText<K>;
}

// Rich text: tag renderers turn `<link>…</link>` into nodes, so the result is a fragment of chunks.
export function renderCatalogMessage(intl: IntlShape, id: string, values?: RichValues): ReactNode {
  if (!hasMessage(intl, id)) {
    return '';
  }
  const formatted = intl.formatMessage({ id }, values);
  return Array.isArray(formatted) ? createElement(Fragment, null, ...formatted) : formatted;
}
